import d3 from "d3";
import qs from "qs";
import React from "react";
import { ArticleTeaser } from "../components/ArticleTeaser";
import KVListComponent from "../components/KVListComponent";
import OrganizationDimensionDevelopmentChart from "../components/OrganizationDimensionDevelopmentChart";
import { PageHeaderJS } from "../components/PageHeader";
import Rating from "../components/Rating";
import ResizableViolinGrid from "../components/ResizableViolinGrid";
import SocialMediaToolbar from "../components/SocialMediaToolbar";
import SocialSharingToolbar from "../components/SocialSharingToolbar";
import TabbedContent from "../components/TabbedContent";
import TabbedControl from "../components/TabbedControl";
import TagFilter from "../components/TagFilter";
import Config from "../config";
import { Deployment } from "../domain/deployment";
import * as Dims from "../domain/dimension";
import { I18nContext, useI18n } from "../domain/i18n";
import { NewsItem } from "../domain/news";
import {
  dimValueDevelopment,
  findPrecedingOrganization,
  findSucceedingOrganization,
  linkToOrganization,
  Organization,
  OrganizationR,
  Year,
} from "../domain/organization";
import { useForceUpdate } from "../hooks/useForceUpdate";
import { DetailH, newDetailH, SelectedYear } from "../state/DetailH";

const parseSelectedYear = (str: string): SelectedYear => {
  if (str === "dev") {
    return { type: "Development" };
  } else if (!isNaN(parseInt(str, 10))) {
    return { type: "Year", year: str };
  } else {
    return { type: "Development" };
  }
};

/**
 * Legacy alarm:
 * This hook uses force updates internally to propagate state changes from the
 * state instance to React. We also need to make sure to fetch data only once,
 * hence the non-exhaustive hook dependency array.
 */
function useDetailState(deployment: Deployment, orgId: string) {
  const I18n = useI18n();
  const query = qs.parse(location.search.substring(1));
  const selectedYear = parseSelectedYear(query && typeof query.year === "string" ? query.year : "");
  const forceUpdate = useForceUpdate();
  const [state] = React.useState(() =>
    newDetailH(deployment, I18n.locale, orgId, selectedYear, () => {
      forceUpdate();
    }),
  );
  React.useEffect(() => {
    state.fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}

export interface OrganizationDetailTemplateProps {
  organization: OrganizationR;
  organizations: Array<OrganizationR>;
  articles: Array<NewsItem>;
  deployment: Deployment;
}
export default function OrganizationDetailTemplate({
  organization,
  organizations,
  articles,
  deployment,
}: OrganizationDetailTemplateProps) {
  const I18n = useI18n();
  const state = useDetailState(deployment, organization.id);
  const orgRes = state.organizationResource;

  const precedingOrg = findPrecedingOrganization(organizations, organization);
  const succeedingOrg = findSucceedingOrganization(organizations, organization);

  return (
    <>
      <PageHeaderJS
        title={organization.name}
        toggle
        previous_url={precedingOrg ? linkToOrganization(I18n.locale, precedingOrg) : undefined}
        previous_label={"Vorherige Organisation"}
        next_url={succeedingOrg ? linkToOrganization(I18n.locale, succeedingOrg) : undefined}
        next_label={"Nächste Organisation"}
        toolbar_left={<ToolbarLeft selectedTab={state.selectedTab} setSelectedTab={state.setSelectedTab} />}
        toolbar_right={
          <TabbedControl
            items={[
              {
                key: "feed",
                name: I18n.t("frontend.tabs.feed.label"),
                icon: "feed",
              },
              {
                key: "share",
                name: I18n.t("frontend.tabs.share.label"),
                icon: "share",
              },
            ]}
            selectedItem={state.selectedTab}
            onChange={state.setSelectedTab}
          />
        }
        tabbed_content_left={
          state.organizationResource.type === "RDone" ? (
            <TabbedContentLeft state={state} org={state.organizationResource.data} />
          ) : undefined
        }
        tabbed_content_right={<TabbedContentRight state={state} />}
        organizations={organizations}
      />

      <div className="layout-content group">
        <div className="span-3of5">
          <div data-view="detail">
            {orgRes.type === "REmpty" ? (
              <div className="loading loading--vm loading--left" />
            ) : orgRes.type === "RLoading" ? (
              <div className="loading loading--vm loading--left" />
            ) : orgRes.type === "RFailed" ? (
              <div className="error">{I18n.t("frontend.detail.missing-data")}</div>
            ) : (
              <div className={deployment}>
                <DetailView state={state} org={orgRes.data} deployment={deployment} />
              </div>
            )}
          </div>
        </div>

        <div className="span-2of5 plxxl">
          <h3 className="heading-section mbxl">{I18n.t("organization.related.title")}</h3>

          {articles.length > 0 ? (
            <div className="teaser-list">
              {articles.map((item) => (
                <ArticleTeaser key={`article-${item.id}`} item={item} />
              ))}
            </div>
          ) : (
            <div className="context-text">
              <p>
                <em>{I18n.t("organization.related.no-results")}</em>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------

const isDataAvailable = (org: Organization, selectedYear: SelectedYear) => {
  if (selectedYear.type === "Year" && org && org.availableYears.indexOf(selectedYear.year) === -1) {
    return false;
  } else if (selectedYear.type === "Development" && org && org.availableYears.length < 2) {
    return false;
  } else {
    return true;
  }
};

const dimensionSelectedYear = (I18n: I18nContext, state: DetailH) => {
  const { resetSelectedYear, selectedYear, mostRecentYear } = state;
  if (selectedYear.type === "Development") {
    return [
      I18n.t("frontend.detail.title.suffix.development.prefix"),
      " ",
      <span key="development" className="filter-description__clear" onClick={resetSelectedYear}>
        {I18n.t("frontend.detail.title.suffix.development.action")}
      </span>,
    ];
  } else if (selectedYear.year === mostRecentYear) {
    return <span />;
  } else {
    return [
      I18n.t("frontend.detail.title.suffix.year.prefix"),
      " ",
      <span key="year" className="filter-description__clear" onClick={resetSelectedYear}>
        {I18n.t("frontend.detail.title.suffix.year.action", { year: selectedYear.year })}
      </span>,
    ];
  }
};

const violinDataForYear = (I18n: I18nContext, org: Organization): any => {
  return org.dimensions.map((dimensionData) => {
    return {
      id: dimensionData.key,
      name: I18n.t("frontend.dimensions." + dimensionData.key),
      dimensions: org.dimensions,
      dimKey: dimensionData.key,
      delta: org.delta,
    };
  });
};

const maxYDeviation = (_state: DetailH, org: Organization) => {
  let dim, first: any, j, len, max: any, values;
  max = 0;
  const ref = Dims.dimensions({
    js: true,
  });
  for (j = 0, len = ref.length; j < len; j++) {
    dim = ref[j];
    values = dimValueDevelopment(org, dim.key);
    if (values.length > 1) {
      first = values[0];
      values.slice(1).forEach((x) => {
        return (max = Math.max(Math.abs(x.value - first.value), max));
      });
    }
  }
  return max;
};

const mkXScale = (_state: DetailH, org: Organization) => {
  let dim, j, len, max, min, values;
  min = +2e308;
  max = -2e308;
  const ref = Dims.dimensions({
    js: true,
  });
  for (j = 0, len = ref.length; j < len; j++) {
    dim = ref[j];
    values = dimValueDevelopment(org, dim.key);
    min = Math.min(
      min,
      d3.min(values, (x) => +x.year),
    );
    max = Math.max(
      max,
      d3.max(values, (x) => +x.year),
    );
  }
  return d3.scale.linear().domain([min, max]);
};

// -----------------------------------------------------------------------------

const DetailHeading = ({ state, org }: { state: DetailH; org: Organization }) => {
  const I18n = useI18n();
  return (
    <h2 className="heading-section mbxl">
      <div className="filter-description">
        {I18n.t("frontend.detail.title.main", { name: org.name, suffix: "" })}
        {dimensionSelectedYear(I18n, state)}
      </div>
    </h2>
  );
};

const NoYearDataDetail = ({ state, org, year }: { state: DetailH; org: Organization; year: Year }) => {
  const I18n = useI18n();
  return (
    <div>
      <DetailHeading state={state} org={org} />
      <p>{I18n.t("frontend.detail.missing-year", { year })}</p>
    </div>
  );
};

const NoDevelopmentDetail = ({ state, org }: { state: DetailH; org: Organization }) => {
  const I18n = useI18n();
  return (
    <div>
      <DetailHeading state={state} org={org} />
      <p>{I18n.t("frontend.detail.no-development")}</p>
    </div>
  );
};

const IndustryComparisonSection = ({
  deployment,
  state,
  org,
  year,
}: {
  deployment: Deployment;
  state: DetailH;
  org: Organization;
  year: Year;
}) => {
  const I18n = useI18n();
  return (
    <div>
      <h2 className="heading-section mtxxl mbxl">{I18n.t("frontend.detail.industry-comparison.title", org)}</h2>
      <div>
        <ResizableViolinGrid
          deployment={deployment}
          mode={"list"}
          dims={Dims.withSelected(Dims.initial(), Dims.summaryKey)}
          showSummaryDimAsOutline={false}
          selectedViolinId={org.id}
          valueFormat={Config.valueFormat}
          organizationsData={state.industryOrganizations}
          showLineRuleAvg={true}
          showLinksToDetailPage={true}
          showAvgValueText={true}
          showRanks={true}
          enableTooltip={true}
          selectedYear={year}
        />
      </div>
    </div>
  );
};

const YearDetail = ({
  deployment,
  state,
  org,
  year,
}: {
  deployment: Deployment;
  state: DetailH;
  org: Organization;
  year: Year;
}) => {
  const I18n = useI18n();
  return (
    <div>
      <DetailHeading state={state} org={org} />
      <div className="detail__dimensions-unfolded">
        <ResizableViolinGrid
          deployment={deployment}
          mode={"list"}
          dims={(d: any) => Dims.withSelected(Dims.initial(), d.dimKey)}
          showSummaryDimAsOutline={true}
          valueFormat={Config.valueFormat}
          showLinksToDetailPage={false}
          showAvgValueText={true}
          showRanks={false}
          showDelta
          showLineRuleAvg={true}
          lineRuleAvg={(() => {
            const dv = org.dimensions.dimValues(Dims.summaryKey);
            return dv ? dv.avg : undefined;
          })()}
          lineRuleTitle=""
          enableTooltip={true}
          organizationsData={violinDataForYear(I18n, org)}
          suppressTransitions={true}
          selectedYear={year}
        />
      </div>
      <IndustryComparisonSection state={state} org={org} year={year} deployment={deployment} />
    </div>
  );
};

const DevelopmentDetail = ({ state, org }: { state: DetailH; org: Organization }) => {
  const I18n = useI18n();
  const xScale = mkXScale(state, org);
  const maxYDev = maxYDeviation(state, org);
  const dimensions: any[] = Dims.dimensions({ js: true }).map(Dims.localizeDim(I18n));
  return (
    <div>
      <DetailHeading state={state} org={org} />
      {dimensions.map((dim, i) => (
        <OrganizationDimensionDevelopmentChart
          key={i}
          xScale={xScale}
          maxYDeviation={maxYDev}
          org={org}
          dim={dim}
          isFirst={i === 0}
        />
      ))}
    </div>
  );
};

function DetailView({ deployment, state, org }: { deployment: Deployment; state: DetailH; org: Organization }) {
  const { selectedYear } = state;
  return !isDataAvailable(org, selectedYear) && selectedYear.type === "Year" ? (
    <NoYearDataDetail state={state} org={org} year={selectedYear.year} />
  ) : !isDataAvailable(org, selectedYear) ? (
    <NoDevelopmentDetail state={state} org={org} />
  ) : selectedYear.type === "Year" ? (
    <YearDetail state={state} org={org} year={selectedYear.year} deployment={deployment} />
  ) : (
    <DevelopmentDetail state={state} org={org} />
  );
}

function ToolbarLeft({
  selectedTab,
  setSelectedTab,
}: {
  selectedTab: string;
  setSelectedTab: (selectedTab: string) => void;
}) {
  const I18n = useI18n();
  const items = Config.yearSelectionEnabled
    ? [
        {
          key: "years",
          name: I18n.t("frontend.tabs.years.label"),
        },
        {
          key: "details",
          name: I18n.t("frontend.tabs.details.label"),
        },
        {
          key: "links",
          name: I18n.t("frontend.tabs.links.label"),
        },
      ]
    : [
        {
          key: "details",
          name: I18n.t("frontend.tabs.details.label"),
        },
        {
          key: "links",
          name: I18n.t("frontend.tabs.links.label"),
        },
      ];
  return <TabbedControl items={items} selectedItem={selectedTab} onChange={setSelectedTab} />;
}

function YearsContent(props: { tabKey: string; state: DetailH }) {
  const I18n = useI18n();
  const { setSelectedYear, selectedYear, availableYears } = props.state;
  const tags = availableYears
    .slice(0)
    .reverse()
    .map((year) => ({
      key: year,
      name: year,
      selectionValue: { type: "Year", year } as SelectedYear,
    }));
  if (availableYears.length > 1) {
    tags.push({
      key: "development",
      name: I18n.t("frontend.detail.development.label"),
      selectionValue: { type: "Development" } as SelectedYear,
    });
  }
  const onClick = (tag: any) => {
    setSelectedYear(tag.selectionValue);
  };
  return (
    <TagFilter
      modifiers={"tag-filter--standalone"}
      tabKey={props.tabKey}
      tabTitle=""
      tags={tags}
      selectedTags={[selectedYear.type === "Year" ? selectedYear.year : "development"]}
      onClick={onClick}
    />
  );
}

// -----------------------------------------------------------------------------

const formatMapUrl = (org: Organization) => {
  const location = (
    org.headquarters_street +
    "," +
    org.headquarters_city +
    "," +
    org.headquarter_object.country
  ).replace(/\s+/, "+");
  return "https://www.google.com/maps/place/" + location;
};

function TabbedContentLeft({ state, org }: { state: DetailH; org: Organization }) {
  const I18n = useI18n();
  return (
    <TabbedContent selectedTab={state.selectedTab}>
      <YearsContent tabKey="years" state={state} />
      <KVListComponent
        tabKey="details"
        items={
          [
            /* HACK: Disable ratings in the Leipzig Atlas */
            org.orgResponses[0].region_id === "LE"
              ? null
              : {
                  key: I18n.t("frontend.tabs.details.group"),
                  value: <Rating rating={org.rating} dimKey={undefined} showLabel />,
                },
            {
              key: I18n.t("frontend.tabs.details.sector"),
              value: org.sector_object.name,
            },
            {
              key: I18n.t("frontend.tabs.details.industry"),
              value: org.industry_object.name,
            },
          ].filter(Boolean) as any
        }
      />
      <KVListComponent
        tabKey="links"
        items={[
          {
            key: I18n.t("frontend.tabs.links.address"),
            value: I18n.t("frontend.detail.address-format", org),
            valueUrl: formatMapUrl(org),
            htmlAttrs: {
              target: "_blank",
            },
          },
          {
            key: I18n.t("frontend.tabs.links.website"),
            value: org.website,
            valueUrl: org.website,
            htmlAttrs: {
              target: "_blank",
            },
          },
        ]}
      />
    </TabbedContent>
  );
}

function TabbedContentRight({ state }: { state: DetailH }) {
  return (
    <TabbedContent selectedTab={state.selectedTab}>
      <SocialMediaToolbar tabKey="feed" links={{ rss: Config.feedUrl }} />
      <SocialSharingToolbar tabKey="share" />
    </TabbedContent>
  );
}
